/**
 * Sélecteur de récurrence — le composant le plus difficile du produit
 *
 * Deux principes de conception :
 *
 *  1. **Une phrase de confirmation en français, en permanence.** « Tous les
 *     2 ans en septembre — prochaines : sept. 2026, sept. 2028 ». C'est ce qui
 *     rend le composant utilisable sans documentation : on lit ce qu'on a réglé
 *     au lieu de le déduire d'une grille de cases.
 *
 *  2. **La base de calcul est exposée, pas devinée.** Repartir de la date prévue
 *     ou de la date de réalisation change tout : un contrôle technique reste calé
 *     sur le calendrier, un détartrage fait trois mois en retard décale la suite.
 *     Une phrase explique, l'utilisateur choisit.
 *
 * Le calcul de l'aperçu vient du même noyau que l'API : ce qui s'affiche ici est
 * exactement ce qui sera écrit en base.
 */

import { useMemo } from 'react';

import {
  type Ancrage,
  type BaseRecalcul,
  type CodeJour,
  type PeriodeVacances,
  type Recurrence,
  ANCRAGES,
  LIBELLE_ANCRAGE,
  MOIS_LONG,
  apercuOccurrences,
  decrisRecurrence,
  enCourt,
} from '@foyer/noyau';

import { Champ } from './ui.js';

export type Mode = 'aucune' | 'jour' | 'semaine' | 'mois' | 'an' | 'vacances';

export interface EtatRecurrence {
  mode: Mode;
  interval: number;
  /** Mode hebdomadaire : jours cochés. */
  jours: CodeJour[];
  /** Mode mensuel. */
  varianteMois: 'jourDuMois' | 'rang' | 'dernier';
  jourDuMois: number;
  rang: number;
  jourDuRang: CodeJour;
  /** Mode annuel : mois cible, ou `null` pour la date de départ. */
  moisCible: number | null;
  ancrage: Ancrage;
  base: BaseRecalcul;
}

const JOURS: { code: CodeJour; court: string; long: string }[] = [
  { code: 'MO', court: 'L', long: 'lundi' },
  { code: 'TU', court: 'M', long: 'mardi' },
  { code: 'WE', court: 'M', long: 'mercredi' },
  { code: 'TH', court: 'J', long: 'jeudi' },
  { code: 'FR', court: 'V', long: 'vendredi' },
  { code: 'SA', court: 'S', long: 'samedi' },
  { code: 'SU', court: 'D', long: 'dimanche' },
];

export const RECURRENCE_INITIALE: EtatRecurrence = {
  mode: 'aucune',
  interval: 1,
  jours: ['SA'],
  varianteMois: 'jourDuMois',
  jourDuMois: 1,
  rang: 1,
  jourDuRang: 'SA',
  moisCible: null,
  ancrage: 'milieu_vacances',
  base: 'echeance',
};

/** Traduit l'état de l'interface en récurrence stockable. */
export function versRecurrence(etat: EtatRecurrence, depart: string): Recurrence {
  if (etat.mode === 'aucune') {
    return { rrule: null, depart: null, base: etat.base, ancrage: null };
  }
  if (etat.mode === 'vacances') {
    return { rrule: null, depart: null, base: etat.base, ancrage: etat.ancrage };
  }

  const bouts: string[] = [];
  const interval = Math.max(1, Math.trunc(etat.interval));

  switch (etat.mode) {
    case 'jour':
      bouts.push('FREQ=DAILY');
      break;
    case 'semaine':
      bouts.push('FREQ=WEEKLY');
      if (etat.jours.length > 0) bouts.push(`BYDAY=${etat.jours.join(',')}`);
      break;
    case 'mois':
      bouts.push('FREQ=MONTHLY');
      if (etat.varianteMois === 'dernier') bouts.push('BYMONTHDAY=-1');
      else if (etat.varianteMois === 'rang') bouts.push(`BYDAY=${etat.rang}${etat.jourDuRang}`);
      else bouts.push(`BYMONTHDAY=${etat.jourDuMois}`);
      break;
    case 'an':
      bouts.push('FREQ=YEARLY');
      if (etat.moisCible !== null) bouts.push(`BYMONTH=${etat.moisCible + 1}`);
      break;
  }

  if (interval !== 1) bouts.splice(1, 0, `INTERVAL=${interval}`);
  return { rrule: bouts.join(';'), depart, base: etat.base, ancrage: null };
}

/** Reconstitue l'état de l'interface depuis une tâche existante. */
export function depuisRecurrence(rrule: string | null, ancrage: Ancrage | null, base: BaseRecalcul): EtatRecurrence {
  if (ancrage) return { ...RECURRENCE_INITIALE, mode: 'vacances', ancrage, base };
  if (!rrule) return { ...RECURRENCE_INITIALE, base };

  const morceaux = new Map(
    rrule.split(';').map((m) => {
      const i = m.indexOf('=');
      return [m.slice(0, i).toUpperCase(), m.slice(i + 1)] as const;
    }),
  );
  const etat: EtatRecurrence = { ...RECURRENCE_INITIALE, base };
  etat.interval = Number(morceaux.get('INTERVAL') ?? 1);

  const freq = morceaux.get('FREQ');
  if (freq === 'DAILY') etat.mode = 'jour';
  else if (freq === 'WEEKLY') {
    etat.mode = 'semaine';
    const jours = morceaux.get('BYDAY');
    if (jours) etat.jours = jours.split(',').map((j) => j.replace(/[^A-Z]/g, '') as CodeJour);
  } else if (freq === 'MONTHLY') {
    etat.mode = 'mois';
    const jm = morceaux.get('BYMONTHDAY');
    const jour = morceaux.get('BYDAY');
    if (jm === '-1') etat.varianteMois = 'dernier';
    else if (jour) {
      etat.varianteMois = 'rang';
      etat.rang = Number(jour.replace(/[^-\d]/g, '')) || 1;
      etat.jourDuRang = jour.replace(/[^A-Z]/g, '') as CodeJour;
    } else if (jm) {
      etat.varianteMois = 'jourDuMois';
      etat.jourDuMois = Number(jm);
    }
  } else if (freq === 'YEARLY') {
    etat.mode = 'an';
    const mois = morceaux.get('BYMONTH');
    etat.moisCible = mois ? Number(mois) - 1 : null;
  }
  return etat;
}

export function SelecteurRecurrence({
  etat,
  onChange,
  depart,
  periodes = [],
}: {
  etat: EtatRecurrence;
  onChange(e: EtatRecurrence): void;
  /** Date de départ de la série : sert à l'aperçu. */
  depart: string;
  periodes?: PeriodeVacances[];
}) {
  const modifie = (partiel: Partial<EtatRecurrence>) => onChange({ ...etat, ...partiel });

  const { phrase, occurrences } = useMemo(() => {
    const recurrence = versRecurrence(etat, depart);
    try {
      return {
        phrase: decrisRecurrence(recurrence),
        occurrences: etat.mode === 'aucune' ? [] : apercuOccurrences(recurrence, depart, 3, periodes),
      };
    } catch (err) {
      return { phrase: `Règle invalide : ${(err as Error).message}`, occurrences: [] };
    }
  }, [etat, depart, periodes]);

  return (
    <div>
      <Champ etiquette="Répétition">
        <div className="puces" role="group" aria-label="Type de répétition">
          {(
            [
              ['aucune', 'Aucune'],
              ['jour', 'Quotidien'],
              ['semaine', 'Hebdo'],
              ['mois', 'Mensuel'],
              ['an', 'Annuel'],
              ['vacances', 'Vacances'],
            ] as [Mode, string][]
          ).map(([mode, libelle]) => (
            <button
              key={mode}
              type="button"
              className={`puce${etat.mode === mode ? ' active' : ''}`}
              onClick={() => modifie({ mode })}
              aria-pressed={etat.mode === mode}
            >
              {libelle}
            </button>
          ))}
        </div>
      </Champ>

      {etat.mode !== 'aucune' && etat.mode !== 'vacances' && (
        <div className="rangee">
          <Champ etiquette="Tous les">
            <input
              type="number"
              min={1}
              max={99}
              value={etat.interval}
              onChange={(e) => modifie({ interval: Number(e.target.value) || 1 })}
              aria-label="Intervalle"
            />
          </Champ>
          <div style={{ alignSelf: 'end', paddingBottom: 14, flex: '2 1 200px' }} className="sourdine">
            {etat.mode === 'jour' && (etat.interval === 1 ? 'jour' : 'jours')}
            {etat.mode === 'semaine' && (etat.interval === 1 ? 'semaine' : 'semaines')}
            {etat.mode === 'mois' && 'mois'}
            {etat.mode === 'an' && (etat.interval === 1 ? 'an' : 'ans')}
          </div>
        </div>
      )}

      {etat.mode === 'semaine' && (
        <Champ etiquette="Jours de la semaine">
          <div className="puces" role="group" aria-label="Jours de la semaine">
            {JOURS.map((j) => {
              const coche = etat.jours.includes(j.code);
              return (
                <button
                  key={j.code}
                  type="button"
                  className={`puce${coche ? ' active' : ''}`}
                  style={{ minWidth: 40 }}
                  aria-pressed={coche}
                  aria-label={j.long}
                  onClick={() =>
                    modifie({
                      jours: coche ? etat.jours.filter((c) => c !== j.code) : [...etat.jours, j.code],
                    })
                  }
                >
                  {j.court}
                </button>
              );
            })}
          </div>
        </Champ>
      )}

      {etat.mode === 'mois' && (
        <>
          <Champ etiquette="Dans le mois">
            <select
              value={etat.varianteMois}
              onChange={(e) => modifie({ varianteMois: e.target.value as EtatRecurrence['varianteMois'] })}
            >
              <option value="jourDuMois">un jour du mois</option>
              <option value="rang">un rang dans le mois</option>
              <option value="dernier">le dernier jour du mois</option>
            </select>
          </Champ>
          {etat.varianteMois === 'jourDuMois' && (
            <Champ
              etiquette="Le"
              aide={
                etat.jourDuMois >= 29
                  ? 'Les mois plus courts sont sautés. Pour « fin de mois », choisir « le dernier jour du mois ».'
                  : undefined
              }
            >
              <input
                type="number"
                min={1}
                max={31}
                value={etat.jourDuMois}
                onChange={(e) => modifie({ jourDuMois: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })}
              />
            </Champ>
          )}
          {etat.varianteMois === 'rang' && (
            <div className="rangee">
              <Champ etiquette="Rang">
                <select value={etat.rang} onChange={(e) => modifie({ rang: Number(e.target.value) })}>
                  <option value={1}>le premier</option>
                  <option value={2}>le deuxième</option>
                  <option value={3}>le troisième</option>
                  <option value={4}>le quatrième</option>
                  <option value={-1}>le dernier</option>
                </select>
              </Champ>
              <Champ etiquette="Jour">
                <select
                  value={etat.jourDuRang}
                  onChange={(e) => modifie({ jourDuRang: e.target.value as CodeJour })}
                >
                  {JOURS.map((j) => (
                    <option key={j.code} value={j.code}>
                      {j.long}
                    </option>
                  ))}
                </select>
              </Champ>
            </div>
          )}
        </>
      )}

      {etat.mode === 'an' && (
        <Champ etiquette="Fenêtre saisonnière" aide="Indispensable pour le ramonage ou l'entretien avant l'hiver.">
          <select
            value={etat.moisCible === null ? '' : String(etat.moisCible)}
            onChange={(e) => modifie({ moisCible: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <option value="">à la date de départ</option>
            {MOIS_LONG.map((nom, i) => (
              <option key={nom} value={i}>
                en {nom}
              </option>
            ))}
          </select>
        </Champ>
      )}

      {etat.mode === 'vacances' && (
        <Champ
          etiquette="Ancrage"
          aide={
            periodes.length === 0
              ? 'Le calendrier scolaire n’est pas encore synchronisé : l’échéance ne pourra pas être calculée.'
              : undefined
          }
        >
          <select value={etat.ancrage} onChange={(e) => modifie({ ancrage: e.target.value as Ancrage })}>
            {ANCRAGES.map((a) => (
              <option key={a} value={a}>
                {LIBELLE_ANCRAGE[a]}
              </option>
            ))}
          </select>
        </Champ>
      )}

      {etat.mode !== 'aucune' && (
        <Champ etiquette="Base de calcul">
          <select value={etat.base} onChange={(e) => modifie({ base: e.target.value as BaseRecalcul })}>
            <option value="echeance">Repartir de la date prévue</option>
            <option value="realisation">Repartir de la date de réalisation</option>
          </select>
          <div className="aide">
            {etat.base === 'echeance'
              ? 'La suite reste calée sur le calendrier : cocher en retard ne décale pas les échéances suivantes.'
              : 'Le compte repart du jour où c’est réellement fait : un retard décale toute la suite.'}
          </div>
        </Champ>
      )}

      {/* La phrase de confirmation, toujours visible : c'est elle qui rend le
          composant utilisable sans documentation. */}
      <div className={phrase.startsWith('Règle invalide') ? 'alerte' : 'succes'}>
        {phrase}
        {occurrences.length > 0 && (
          <div style={{ fontWeight: 400, marginTop: 4 }}>
            Prochaines : {occurrences.map((d) => enCourt(d)).join(' · ')}
          </div>
        )}
        {etat.mode !== 'aucune' && occurrences.length === 0 && (
          <div style={{ fontWeight: 400, marginTop: 4 }}>Aucune occurrence calculable.</div>
        )}
      </div>
    </div>
  );
}
